import { Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTenant } from '../../../contexts/TenantContext';
import AdminPage from '../layout/AdminPage';

const WEEK = [
  ['MONDAY', 'Hétfő'],
  ['TUESDAY', 'Kedd'],
  ['WEDNESDAY', 'Szerda'],
  ['THURSDAY', 'Csütörtök'],
  ['FRIDAY', 'Péntek'],
  ['SATURDAY', 'Szombat'],
  ['SUNDAY', 'Vasárnap'],
];

const WEEKDAY_LABELS = Object.fromEntries(WEEK);

const range = (start, end) => `${start}–${end}`;

const Section = ({ title, testId, children }) => (
  <Paper variant="outlined" data-testid={testId} sx={{ borderRadius: 3, overflow: 'hidden' }}>
    <Typography component="h2" sx={{ fontWeight: 700, fontSize: '1rem', px: 2.5, pt: 2.5, pb: 1 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

/**
 * The tenant's week (DECISIONS.md D33) and its daily breaks (D32), read-only: both are set in the
 * database for now, so this page shows the admin what the booking page offers and why a slot is
 * missing. Everything comes from the Tenant DTO; there is no endpoint of its own.
 */
const OpeningHoursPage = () => {
  const { openingHours, dailyBreaks } = useTenant();
  const hoursByDay = Object.fromEntries(openingHours.map((day) => [day.dayOfWeek, day]));

  return (
    <AdminPage
      title="Nyitvatartás és szünetek"
      subtitle="Ezekben az időkben foglalhatnak a vendégek. A napi szünetbe eső időpontokat a foglalási oldal nem kínálja fel, kézi foglalással viszont oda is lehet foglalni."
    >
      <Stack spacing={3}>
        <Section title="Nyitvatartás" testId="opening-hours-week">
          <Table size="small">
            <TableBody>
              {WEEK.map(([dayOfWeek, label]) => {
                const day = hoursByDay[dayOfWeek];
                return (
                  <TableRow key={dayOfWeek} data-testid={`opening-hours-${dayOfWeek}`}>
                    <TableCell sx={{ fontWeight: 500, pl: 2.5 }}>{label}</TableCell>
                    <TableCell sx={{ pr: 2.5, color: day ? 'text.primary' : 'text.secondary' }}>
                      {day ? range(day.openingTime, day.closingTime) : 'Zárva'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Section>

        <Section title="Napi szünetek" testId="daily-breaks">
          {dailyBreaks.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', px: 2.5, pb: 2.5 }}>
              Nincs napi szünet.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ pl: 2.5 }}>Mikor</TableCell>
                  <TableCell sx={{ pr: 2.5 }}>Időszak</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dailyBreaks.map((dailyBreak) => (
                  <TableRow key={`${dailyBreak.dayOfWeek ?? 'ALL'}-${dailyBreak.startTime}`}>
                    <TableCell sx={{ fontWeight: 500, pl: 2.5 }}>
                      {dailyBreak.dayOfWeek ? WEEKDAY_LABELS[dailyBreak.dayOfWeek] : 'Minden nyitvatartási nap'}
                    </TableCell>
                    <TableCell sx={{ pr: 2.5 }}>{range(dailyBreak.startTime, dailyBreak.endTime)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          A nyitvatartást és a szüneteket innen még nem lehet módosítani. Ha változnak, szólj a
          rendszer üzemeltetőjének.
        </Typography>
      </Stack>
    </AdminPage>
  );
};

export default OpeningHoursPage;
